/** A small DOM/SVG layer above the renderer for the ranged aim board. */

import type { RangedAimReadout } from './ranged-aim.js';

interface OverlayView {
  readonly from: { x: number; y: number };
  readonly target: { x: number; y: number };
  readonly camera: { x: number; y: number };
  readonly viewport: { width: number; height: number; cellWidth: number; cellHeight: number };
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The canvas renderers intentionally know nothing about UI chrome. This lives
 * above them, so Canvas2D and WebGL draw the same aim line and target bracket.
 */
export class RangedAimOverlay {
  private readonly root: SVGSVGElement | null;

  constructor(canvas: HTMLCanvasElement) {
    const parent = canvas.parentElement;
    if (!parent) {
      this.root = null;
      return;
    }
    if (globalThis.getComputedStyle(parent).position === 'static')
      parent.style.position = 'relative';

    const root = document.createElementNS(SVG_NS, 'svg');
    root.setAttribute('aria-hidden', 'true');
    root.style.position = 'absolute';
    root.style.inset = '0';
    root.style.pointerEvents = 'none';
    root.style.overflow = 'visible';
    root.style.display = 'none';
    parent.append(root);
    this.root = root;
  }

  show(readout: RangedAimReadout, view: OverlayView): void {
    const root = this.root;
    if (!root) return;
    const ratio =
      view.viewport.width / Math.max(1, root.parentElement?.clientWidth ?? view.viewport.width);
    const width = view.viewport.width / ratio;
    const height = view.viewport.height / ratio;
    root.setAttribute('viewBox', `0 0 ${width} ${height}`);
    root.setAttribute('width', String(width));
    root.setAttribute('height', String(height));
    root.replaceChildren();

    const originX = Math.round(
      view.camera.x * view.viewport.cellWidth +
        view.viewport.cellWidth / 2 -
        view.viewport.width / 2,
    );
    const originY = Math.round(
      view.camera.y * view.viewport.cellHeight +
        view.viewport.cellHeight / 2 -
        view.viewport.height / 2,
    );
    const point = (tile: { x: number; y: number }) => ({
      x: (tile.x * view.viewport.cellWidth - originX + view.viewport.cellWidth / 2) / ratio,
      y: (tile.y * view.viewport.cellHeight - originY + view.viewport.cellHeight / 2) / ratio,
    });
    const start = point(view.from);
    const end = point(view.target);
    const colour = readout.clear ? '#e8c75d' : '#cc5a4f';

    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', String(start.x));
    line.setAttribute('y1', String(start.y));
    line.setAttribute('x2', String(end.x));
    line.setAttribute('y2', String(end.y));
    line.setAttribute('stroke', colour);
    line.setAttribute('stroke-width', '1.5');
    line.setAttribute('stroke-dasharray', readout.clear ? '3 2' : '2 2');
    root.append(line);

    const cellWidth = view.viewport.cellWidth / ratio;
    const cellHeight = view.viewport.cellHeight / ratio;
    const bracket = document.createElementNS(SVG_NS, 'rect');
    bracket.setAttribute('x', String(end.x - cellWidth / 2 + 1));
    bracket.setAttribute('y', String(end.y - cellHeight / 2 + 1));
    bracket.setAttribute('width', String(cellWidth - 2));
    bracket.setAttribute('height', String(cellHeight - 2));
    bracket.setAttribute('fill', 'none');
    bracket.setAttribute('stroke', colour);
    bracket.setAttribute('stroke-width', '1.5');
    root.append(bracket);

    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('x', String(Math.min(width - 132, Math.max(4, end.x + cellWidth))));
    label.setAttribute('y', String(Math.max(14, end.y - cellHeight / 2)));
    label.setAttribute('fill', colour);
    label.setAttribute('font-size', '11');
    label.setAttribute('font-family', 'monospace');
    label.setAttribute('paint-order', 'stroke');
    label.setAttribute('stroke', '#1a1712');
    label.setAttribute('stroke-width', '3');
    label.textContent = `${readout.range} tiles · ${readout.clear ? 'clear' : 'blocked'} · ${readout.wind}`;
    root.append(label);
    root.style.display = 'block';
  }

  hide(): void {
    if (this.root) this.root.style.display = 'none';
  }

  dispose(): void {
    this.root?.remove();
  }
}
